import React from 'react';
import { IconButton, TextField, Typography, Checkbox } from '@mui/material';
import { ReactComponent as RemoveIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as CheckIcon } from 'new/components/icons/fi-rr-check.svg';
import { Table } from 'new/elements/Table/Table';
import { Column } from 'new/elements/Table/types';
import { Parameter, Header } from '../hooks/useParameterManager';
import cx from 'classnames';
import styles from '../ApiRequestModal.module.scss';

interface ParameterTableProps {
  parameters: Parameter[];
  type: 'path' | 'query' | 'body';
  title: string;
  validationErrors: { [id: number]: string };
  onUpdate: (
    id: number,
    field: string,
    value: string | boolean,
    type: 'path' | 'query' | 'body',
  ) => void;
  onRemove: (id: number, type: 'path' | 'query' | 'body') => void;
}

interface HeaderTableProps {
  mode: 'config' | 'test';
  headers: Header[];
  validationErrors: { [id: number]: string };
  onUpdate: (id: number, field: string, value: string | boolean) => void;
  onRemove: (id: number) => void;
}

export const ParameterTable: React.FC<ParameterTableProps & { mode: 'config' | 'test' }> = ({
  mode,
  parameters,
  type,
  title,
  validationErrors,
  onUpdate,
  onRemove,
}) => {
  const getParameterColumns = (): Column<Parameter>[] => {
    const columns: Column<Parameter>[] = [
      {
        name: 'Key',
        field: 'key',
        width: 120,
        renderRow: (param) => {
          if (!param) return null;
          const hasError = !!validationErrors[param.id];
          return (
            <div className={styles.inputContainer}>
              <TextField
                size="small"
                variant="standard"
                value={param.key || ''}
                onChange={(e) => onUpdate(param.id, 'key', e.target.value, type)}
                className={`${styles.parameterInput} ${hasError ? styles.errorInput : ''}`}
                error={hasError}
                required
                InputProps={{
                  readOnly: mode === 'test',
                  disableUnderline: true,
                  className: styles.parameterText,
                }}
              />
              {hasError && <div className={styles.errorText}>{validationErrors[param.id]}</div>}
            </div>
          );
        },
      },
      {
        name: 'Sample value',
        field: 'value',
        width: 120,
        renderRow: (param) => {
          if (!param) return null;

          // for path parameters, only enable value input if key is dynamic (surrounded by curly braces)
          const isDynamicPathParam =
            type === 'path' && param.key && param.key.startsWith('{') && param.key.endsWith('}');

          const isValueInputDisabled = type === 'path' && !isDynamicPathParam;

          return (
            <div className={cx({ [styles.disabledCell]: isValueInputDisabled })}>
              <TextField
                size="small"
                variant="standard"
                value={!isValueInputDisabled ? param.value : ''}
                onChange={(e) => onUpdate(param.id, 'value', e.target.value, type)}
                className={styles.parameterInput}
                required={!isValueInputDisabled}
                disabled={isValueInputDisabled}
                InputProps={{
                  disableUnderline: true,
                }}
              />
            </div>
          );
        },
      },
    ];
    if (type !== 'path') {
      columns.push({
        name: 'Required?',
        field: 'required',
        width: 80,
        center: true,
        renderRow: (param) => {
          if (!param) return null;
          if (mode === 'config') {
            return (
              <Checkbox
                checked={param.required || false}
                onChange={(e) => onUpdate(param.id, 'required', e.target.checked, type)}
                size="small"
              />
            );
          } else if (param.required) {
            return <CheckIcon />;
          }
        },
      });
    }
    if (mode === 'config') {
      columns.push({
        name: '',
        field: 'actions',
        width: 40,
        center: true,
        renderRow: (param) => {
          if (!param) return null;
          return (
            <IconButton
              size="small"
              onClick={() => onRemove(param.id, type)}
              className={styles.removeButton}
            >
              <RemoveIcon />
            </IconButton>
          );
        },
      });
    }

    return columns;
  };

  return (
    <Table
      columns={getParameterColumns()}
      rows={parameters || []}
      height={50 * (parameters?.length || 0) + 48}
      disableVirtualization={true}
      showCheckbox={false}
      getRowTdClassName={() => styles.rowTdClassName}
      getRowClassName={() => styles.rowClassName}
      headClassName={styles.headRowClassName}
      EmptyComponent={
        <div className={styles.emptyTable}>
          <Typography variant="body2" color="textSecondary">
            No {title.toLowerCase()} added yet
          </Typography>
        </div>
      }
    />
  );
};

export const HeaderTable: React.FC<HeaderTableProps> = ({
  mode,
  headers,
  validationErrors,
  onUpdate,
  onRemove,
}) => {
  const getHeaderColumns = (): Column<Header>[] => {
    const columns: Column<Header>[] = [
      {
        name: 'Key',
        field: 'key',
        width: 120,
        renderRow: (header) => {
          if (!header) return null;
          const hasError = !!validationErrors[header.id];
          return (
            <div className={styles.inputContainer}>
              <TextField
                size="small"
                variant="standard"
                value={header.key || ''}
                onChange={(e) => onUpdate(header.id, 'key', e.target.value)}
                className={`${styles.parameterInput} ${hasError ? styles.errorInput : ''}`}
                error={hasError}
                required
                InputProps={{
                  readOnly: mode === 'test',
                  disableUnderline: true,
                  className: styles.parameterText,
                }}
              />
              {hasError && <div className={styles.errorText}>{validationErrors[header.id]}</div>}
            </div>
          );
        },
      },
      {
        name: 'Sample value',
        field: 'value',
        width: 120,
        renderRow: (header) => {
          if (!header) return null;
          return (
            <TextField
              size="small"
              variant="standard"
              value={header.value || ''}
              onChange={(e) => onUpdate(header.id, 'value', e.target.value)}
              className={styles.parameterInput}
              required
              InputProps={{
                disableUnderline: true,
              }}
            />
          );
        },
      },
      {
        name: 'Required?',
        field: 'required',
        width: 80,
        center: true,
        renderRow: (header) => {
          if (!header) return null;
          if (mode === 'config') {
            return (
              <Checkbox
                checked={header.required || false}
                onChange={(e) => onUpdate(header.id, 'required', e.target.checked)}
                size="small"
              />
            );
          } else if (header.required) {
            return <CheckIcon />;
          }
        },
      },
    ];
    if (mode === 'config') {
      columns.push({
        name: '',
        field: 'actions',
        width: 40,
        center: true,
        renderRow: (header) => {
          if (!header) return null;
          return (
            <IconButton
              size="small"
              onClick={() => onRemove(header.id)}
              className={styles.removeButton}
            >
              <RemoveIcon />
            </IconButton>
          );
        },
      });
    }

    return columns;
  };

  return (
    <Table
      columns={getHeaderColumns()}
      rows={headers || []}
      height={50 * (headers?.length || 0) + 48}
      disableVirtualization={true}
      showCheckbox={false}
      getRowTdClassName={() => styles.rowTdClassName}
      getRowClassName={() => styles.rowClassName}
      headClassName={styles.headRowClassName}
      EmptyComponent={
        <div className={styles.emptyTable}>
          <Typography variant="body2" color="textSecondary">
            No headers added yet
          </Typography>
        </div>
      }
    />
  );
};

interface ResponseHeadersTableProps {
  headers: { [key: string]: string };
}

export const ResponseHeadersTable: React.FC<ResponseHeadersTableProps> = ({ headers }) => {
  const getResponseHeaderColumns = (): Column<{
    key: string;
    value: string;
  }>[] => {
    const columns: Column<{ key: string; value: string }>[] = [
      {
        name: 'Key',
        field: 'key',
        width: 120,
        renderRow: (header) => {
          if (!header) return null;
          return <Typography variant="body2">{header.key}</Typography>;
        },
      },
      {
        name: 'Value',
        field: 'value',
        width: 200,
        renderRow: (header) => {
          if (!header) return null;
          return <Typography variant="body2">{header.value}</Typography>;
        },
      },
    ];

    return columns;
  };

  const headerRows = Object.entries(headers).map(([key, value]) => ({
    key,
    value,
  }));

  return (
    <Table
      columns={getResponseHeaderColumns()}
      rows={headerRows}
      height={50 * (headerRows?.length || 0) + 48}
      disableVirtualization={true}
      showCheckbox={false}
      getRowTdClassName={() => styles.rowTdClassName}
      getRowClassName={() => styles.rowClassName}
      headClassName={styles.headRowClassName}
      EmptyComponent={
        <div className={styles.emptyTable}>
          <Typography variant="body2" color="textSecondary">
            No response headers available
          </Typography>
        </div>
      }
    />
  );
};
